import { useState, useMemo, useEffect } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Free-text category picker with smart suggestions.
 * - Lists common categories AND any custom values previously used.
 * - User can type any value (e.g. "Ferry", "FASTag", "Tolls") — works for edge cases the canonical list doesn't cover.
 * - Optional `mccHints`: shows mcc-derived suggestions inline (e.g. "FASTag · MCC 4784").
 *
 * Props:
 *   value:    string                       — current category string
 *   onChange: (str) => void                — fires with the new category
 *   suggestions?: string[]                 — popular categories shown at top
 *   placeholder?: string
 *   testid?: string
 */
export const CategoryCombobox = ({
  value,
  onChange,
  suggestions = [],
  placeholder = 'Pick or type a category',
  testid = 'category-combobox',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const lower = (s) => (s || '').toLowerCase();
  const filteredSuggestions = useMemo(() => {
    const q = lower(query).trim();
    if (!q) return suggestions;
    return suggestions.filter((s) => lower(s).includes(q));
  }, [suggestions, query]);

  // If the user has typed something that doesn't exactly match any suggestion,
  // surface a "Use X" option so they can commit a custom value with one click.
  const trimmed = query.trim();
  const exactExisting =
    trimmed && suggestions.some((s) => lower(s) === lower(trimmed));
  const showCreateOption = !!trimmed && !exactExisting;

  const commit = (next) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-10 w-full justify-between font-normal mt-1"
          data-testid={testid}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search or type a custom category…"
            data-testid={`${testid}-input`}
          />
          {/* Some browsers (esp. trackpad-driven Mac) won't deliver wheel
              events to the cmdk list when nested under a Radix Popover; the
              parent intercepts scrolling. Forcing `overscroll-behavior: contain`
              + an explicit max-height + `touch-action: pan-y` makes the list
              scrollable with wheel + touchpad reliably. */}
          <CommandList
            className="max-h-[300px] overflow-y-auto"
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
            onWheel={(e) => e.stopPropagation()}
          >
            {filteredSuggestions.length === 0 && !showCreateOption && (
              <CommandEmpty>No matches.</CommandEmpty>
            )}
            {filteredSuggestions.length > 0 && (
              <CommandGroup heading="Suggestions">
                {filteredSuggestions.map((s) => (
                  <CommandItem
                    key={s}
                    value={s}
                    onSelect={() => commit(s)}
                    className="gap-2"
                    data-testid={`${testid}-option-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Check className={cn('h-3.5 w-3.5', lower(value) === lower(s) ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{s}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreateOption && (
              <>
                {filteredSuggestions.length > 0 && <CommandSeparator />}
                <CommandGroup heading="Custom">
                  <CommandItem
                    value={`__use__${trimmed}`}
                    onSelect={() => commit(trimmed)}
                    className="gap-2 text-accent"
                    data-testid={`${testid}-custom-create`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Use “{trimmed}” as category
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
